import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  RouterStateSnapshot,
} from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthenticationServiceBase } from '../../../../libs/modules/tunnelsante/services/authentication-service-base';
import { LoggerService } from '../../../../libs/modules/tunnelsante/services/logger/logger.service';
import { AuthenticationApiModels } from '../../../../models/api/authentication-api.models';
import { AuthenticationModels } from '../../../../models/authentication.models';
import { CommonModels } from '../../../../models/common.models';
import { MtxApiService } from '../services/api/mtx-api.service';
import { MtxDataLoginResponse } from './../services/api/api-service-base';

export abstract class BaseAuthorizationGuard
  implements CanActivate, CanActivateChild
{
  protected abstract expectedAccountKind: CommonModels.AccountKind;

  constructor(
    protected authService:
      | AuthenticationServiceBase<
          AuthenticationModels.Account,
          AuthenticationApiModels.AccountInfoResponse
        >
      | any,
    protected loggerService: LoggerService,
    protected mtxApiService: MtxApiService,
    protected cookieService: CookieService,
  ) {}

  protected abstract navigateOnError(
    account?: AuthenticationModels.Account,
  ): any;

  private getAllRouteLevelsRights(route: ActivatedRouteSnapshot): number[] {
    if (route) {
      let rights: number[] = route.data
        ? route.data['rights']
          ? route.data['rights']
          : []
        : [];

      let parentRights: number[] = [];
      if (route.parent) {
        parentRights = rights.concat(
          this.getAllRouteLevelsRights(route.parent),
        );
      }

      return rights
        .concat(parentRights)
        .filter((value, index, numbers) => numbers.indexOf(value) === index);
    }

    return [];
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean> {
    let logger = this.loggerService.getLogger('Authorization');

    let urlNext = _state.url;

    let isUrlParamOrigin = this.isUrlParamOrigin(urlNext);

    let datasClientMTX = this.cookieService.get('datasClientMTX');

    if (datasClientMTX) {
      datasClientMTX = datasClientMTX.replace(/(%2B)/g, '+');
      datasClientMTX = datasClientMTX.replace(/(<script>.*<\/script>)/g, '');

      let tokenMtx = localStorage.getItem('mtx-token');
      if (tokenMtx) {
        let token: MtxDataLoginResponse = JSON.parse(tokenMtx);
        let exp: number = new Date(token.expiration).getTime();
        let date: number = new Date().getTime();

        const diffInMinutes = Math.floor((exp - date) / (1000 * 60));
        if (diffInMinutes < 10) {
          this.mtxApiService.getToken().subscribe((token) => {
            if (token && token.data && token.data.token) {
              localStorage.setItem('mtx-token', JSON.stringify(token.data));
            }
          });
        }
      } else {
        this.mtxApiService.getToken().subscribe((token) => {
          if (token && token.data && token.data.token) {
            localStorage.setItem('mtx-token', JSON.stringify(token.data));
          }
        });
      }
    } else {
      localStorage.removeItem('mtx-token');
      localStorage.removeItem('dataCleint');
    }

    // debugger; // utile

    if (
      urlNext.includes('souscription-en-ligne') ||
      isUrlParamOrigin ||
      urlNext.includes('param=') ||
      urlNext.includes('?')
    ) {
      return this.authService.loginTunnel(1103).pipe(
        map((bool) => {
          this.authService.getUser().pipe(
            map((user: any) => {
              if (user) {
                logger.log('User found');

                // Bad AccountKind
                if (user.kind !== this.expectedAccountKind) {
                  logger.log(
                    `Bad account kind : got ${user.kind}, expected ${this.expectedAccountKind}`,
                  );
                  this.navigateOnError();
                  return false;
                }

                const rights: number[] = this.getAllRouteLevelsRights(route);
                if (
                  rights &&
                  !rights.every((r) => this.authService.hasRight(r))
                ) {
                  this.navigateOnError(user);
                  return false;
                }

                return true;
              } else {
                logger.log('User was not found in getUser');
                this.navigateOnError();
                return false;
              }
            }),
            catchError((err) => {
              logger.warn('An error occured in getUser', err);
              this.navigateOnError();
              return of(false);
            }),
          );
          return true;
        }),
      );
      //    return of(true);
    }

    sessionStorage.setItem('urlNext', urlNext);
    localStorage.setItem('urlNext', urlNext);
    localStorage.setItem('params', JSON.stringify(route.queryParams));

    return this.authService.getUser().pipe(
      map((user: any) => {
        if (user) {
          logger.log('User found');

          // Bad AccountKind
          if (user.kind !== this.expectedAccountKind) {
            logger.log(
              `Bad account kind : got ${user.kind}, expected ${this.expectedAccountKind}`,
            );
            this.navigateOnError();
            return false;
          }

          const rights: number[] = this.getAllRouteLevelsRights(route);
          if (rights && !rights.every((r) => this.authService.hasRight(r))) {
            this.navigateOnError(user);
            return false;
          }

          return true;
        } else {
          logger.log('User was not found in getUser');
          this.navigateOnError();
          return false;
        }
      }),
      catchError((err) => {
        logger.warn('An error occured in getUser', err);
        this.navigateOnError();
        return of(false);
      }),
    );
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    return this.canActivate(childRoute, state);
  }

  isUrlParamOrigin(urlNext: any): any {
    let isUrlParamOrigin = false;
    const regexpWithoutE = /[/]demande-devis[/](.*)/g;
    const matchParamOrigin = urlNext.match(regexpWithoutE);

    if (matchParamOrigin && matchParamOrigin.length > 0) {
      let urlP = matchParamOrigin[0].split('/')[0];
      if (urlP != 'devis' && urlP != 'tarif') {
        isUrlParamOrigin = true;
      }
    }

    return isUrlParamOrigin;
  }
}
